/**
 * Modern Bold · Cart Drawer behaviours
 * ─────────────────────────────────────────────────────────────────────
 * Layered on top of Shopify's cart-drawer.js + cart.js (which already
 * handle qty updates, line removal, form-based checkout, and Section
 * Rendering API refresh). This file adds:
 *
 *   1. Cross-sell "+" button → POST /cart/add.js, then trigger drawer
 *      refresh by re-rendering #CartDrawer + #cart-icon-bubble via the
 *      Section Rendering API.
 *   2. Order-note autosave (debounced 500ms POST to /cart/update.js).
 *   3. Loading-state class toggle on the body wrapper during async ops.
 *   4. Minus-at-1 routing → when user clicks the minus button while qty
 *      is at the line's minimum (usually 1), trigger remove instead of
 *      letting Dawn stepDown to 0 and getting a server-side error toast.
 *      Also visually disables the minus button at that boundary so the
 *      affordance matches the behaviour.
 *   5. Scroll affordance → `.has-more-below` on the scroll container
 *      while list content remains under the fold (CSS hangs a fade off it).
 *   6. Mobile note placement → below 750px the order-note disclosure is
 *      relocated out of the fixed footer into the scroll area.
 *
 * No dependencies. ES2017+. Defers safely if the drawer isn't on the page.
 */
(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer'; // matches sections/cart-drawer.liquid + the new modern variant
  const SECTIONS_TO_REFRESH = ['cart-drawer', 'cart-icon-bubble'];

  // ── Helpers ──────────────────────────────────────────────────────
  /**
   * Sync the header cart pill from a freshly-rendered section payload.
   * sections/cart-icon-bubble.liquid renders the same <a id="cart-icon-bubble">
   * wrapper as the in-page header, so we replace the live anchor's
   * innerHTML wholesale instead of cherry-picking a single span — the
   * old single-span sync silently broke once we introduced an editorial
   * separator (both spans share aria-hidden="true", and querySelector
   * was matching the separator first → count text turned into "·").
   */
  function syncCartBubble(sectionHtml) {
    if (!sectionHtml) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const fresh = new DOMParser()
      .parseFromString(sectionHtml, 'text/html')
      .querySelector('#cart-icon-bubble');
    if (!fresh) return;
    bubble.innerHTML = fresh.innerHTML;
    const al = fresh.getAttribute('aria-label');
    if (al) bubble.setAttribute('aria-label', al);
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function setLoading(on) {
    const body = document.querySelector('cart-drawer-items');
    if (!body) return;
    body.classList.toggle('is-loading', !!on);
  }

  function refreshDrawerSections() {
    const params = SECTIONS_TO_REFRESH.map((s) => `sections=${encodeURIComponent(s)}`).join('&');
    const url = `${(window.routes && window.routes.cart_url) || '/cart'}?${params}&_=${Date.now()}`;
    return fetch(url, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((state) => {
        if (!state || !state.sections) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && state.sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
          const fresh = parsed.querySelector('#CartDrawer');
          const current = drawer.querySelector('#CartDrawer');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          drawer.classList.remove('is-empty');
        }
        syncCartBubble(state.sections['cart-icon-bubble']);
      });
  }

  function openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  // ── 1. Cross-sell add ────────────────────────────────────────────
  function handleCrossSellClick(event) {
    const btn = event.target.closest('[data-cross-sell-add]');
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    setLoading(true);

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: SECTIONS_TO_REFRESH.join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.sections) {
          // Inline-refresh from the response (saves a round-trip).
          const drawer = document.querySelector('cart-drawer');
          if (drawer && res.sections['cart-drawer']) {
            const parsed = new DOMParser().parseFromString(res.sections['cart-drawer'], 'text/html');
            const fresh = parsed.querySelector('#CartDrawer');
            const current = drawer.querySelector('#CartDrawer');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
            drawer.classList.remove('is-empty');
          }
          syncCartBubble(res.sections['cart-icon-bubble']);
          openDrawer();
        } else {
          return refreshDrawerSections().then(openDrawer);
        }
      })
      .catch(() => {
        btn.disabled = false;
      })
      .finally(() => {
        setLoading(false);
        // Note: the button itself was replaced by the section refresh.
      });
  }

  // ── 2. Order-note autosave ───────────────────────────────────────
  const saveNote = debounce(function (value) {
    fetch(`${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value }),
    }).catch(() => {});
  }, 500);

  function handleNoteInput(event) {
    const ta = event.target.closest('#CartDrawer-Note');
    if (!ta) return;
    saveNote(ta.value);
  }

  // ── 4. Minus-at-1 → remove ───────────────────────────────────────
  // Dawn's quantity-input.js stepDowns to 0 → server returns
  // "minimum of 1" toast which is hostile UX in a drawer. Intercept
  // the click in the CAPTURE phase so we run before Dawn's listener
  // and stopImmediatePropagation prevents stepDown from firing.
  function handleMinusClickCapture(event) {
    const minus = event.target.closest('.mb-cart-drawer__panel button[name="minus"]');
    if (!minus || minus.disabled) return;
    const stepper = minus.closest('quantity-input, .mb-qty-stepper');
    const input = stepper && stepper.querySelector('input[type="number"]');
    if (!input) return;
    const current = parseInt(input.value, 10);
    const min = parseInt(input.dataset.min || input.min || '1', 10) || 1;
    if (!Number.isFinite(current) || current > min) return; // normal stepDown path

    event.preventDefault();
    event.stopImmediatePropagation();
    const item = minus.closest('.cart-item');
    const removeHost = item && item.querySelector('cart-remove-button');
    if (removeHost) {
      // Dispatch a click on the host so the CartRemoveButton listener fires.
      removeHost.click();
    }
  }

  // Sync the disabled visual state of every minus button to its current
  // qty value. Runs on init AND whenever the cart re-renders (DOM change
  // observed via MutationObserver on cart-drawer-items).
  function syncMinusDisabledState(root) {
    const scope = root || document.querySelector('.mb-cart-drawer__panel');
    if (!scope) return;
    scope.querySelectorAll('.mb-qty-stepper').forEach((stepper) => {
      const input = stepper.querySelector('input[type="number"]');
      const minus = stepper.querySelector('button[name="minus"]');
      if (!input || !minus) return;
      const current = parseInt(input.value, 10);
      const min = parseInt(input.dataset.min || input.min || '1', 10) || 1;
      // Don't actually disable() — that would make the click handler not fire.
      // Mark with a data attribute the CSS can hook into.
      minus.toggleAttribute('data-min-reached', current <= min);
    });
  }

  // ── 5. Scroll affordance ─────────────────────────────────────────
  // The item list clips flush against the footer's top border, which
  // reads as the end of the list rather than "more below" — at 390x664
  // the second line item sat entirely off-screen with nothing hinting
  // it was there. Toggles the class the bottom-fade mask hangs off.
  function syncScrollHint() {
    const body = document.querySelector('.mb-cart-drawer__body');
    if (!body) return;
    const room = body.scrollHeight - body.clientHeight;
    body.classList.toggle('has-more-below', room > 2 && body.scrollTop < room - 2);
  }

  // ── 6. Mobile: order note out of the fixed footer ────────────────
  // Below 750px the footer is over half the drawer, and the note was
  // 63px of that closed / 144px open — taken straight out of the item
  // list. The same element moves either way: DetailsDisclosure binds in
  // its constructor, not connectedCallback, so re-parenting neither
  // re-binds nor duplicates its listeners.
  const phone = window.matchMedia('(max-width: 749px)');

  function placeNote() {
    const details = document.querySelector('.mb-cart-drawer__note-disclosure');
    const note = details && details.closest('details-disclosure');
    if (!note) return;
    const target = phone.matches
      ? document.querySelector('[data-mb-note-slot]')
      : document.querySelector('.mb-cart-drawer__foot');
    if (!target || note.parentElement === target) return;

    // Re-parenting blurs whatever the subtree holds, and focus would
    // land on <body>. Hand it back so a customer mid-note keeps it.
    const focused = note.contains(document.activeElement) ? document.activeElement : null;
    if (phone.matches) target.appendChild(note);
    else target.prepend(note);
    if (focused) focused.focus();
  }

  // ── Wire up (event delegation so handlers survive Section Rendering refreshes) ──
  function init() {
    document.addEventListener('click', handleCrossSellClick);
    document.addEventListener('input', handleNoteInput);
    // Capture-phase click to run BEFORE Dawn's quantity-input listener.
    document.addEventListener('click', handleMinusClickCapture, true);
    // Also keep visual state in sync as values change (typing into input).
    document.addEventListener('input', (e) => {
      if (e.target.closest('.mb-cart-drawer__panel .mb-qty-stepper')) syncMinusDisabledState();
    });

    // Scroll doesn't bubble — capture phase catches the drawer's own
    // scrollport without a listener that section refreshes would destroy.
    document.addEventListener(
      'scroll',
      (e) => {
        if (e.target instanceof Element && e.target.classList.contains('mb-cart-drawer__body')) {
          syncScrollHint();
        }
      },
      true
    );
    window.addEventListener('resize', () => {
      placeNote();
      syncScrollHint();
    });

    // Initial pass + observe section-rendering refreshes. A refresh
    // replaces #CartDrawer wholesale, so the note comes back from the
    // server in the footer and has to be re-placed every time.
    syncMinusDisabledState();
    placeNote();
    syncScrollHint();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => {
        syncMinusDisabledState();
        placeNote();
        syncScrollHint();
      }).observe(drawer, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
